export const FORECAST_TAB_TITLE = "Live AI Forecast";
export const MIN_WIN_ACCURACY_SAMPLES = 30;
export const THRESHOLD_LEVELS = [1.5, 2, 3, 5];
export const STRONG_THRESHOLD_PROBABILITY = 0.5;

export const MODEL_DISPLAY = {
  decay_dirichlet: "Dirichlet ML model",
  sliding_window: "Sliding Window ML model",
  ewma: "EWMA model",
};

export function predictionsAllowed(payload) {
  if (!payload) return true;
  const access = payload.access;
  if (access == null) return true;
  if (typeof access === "boolean") return access;
  if (typeof access === "object") return access.predictions !== false;
  return true;
}

export function isDemoPayload(payload) {
  return Boolean(payload && (payload.mode === "demo" || payload.simulated === true));
}

export function parsePredictionMessage(message) {
  if (!message || message.type !== "predictions") return null;
  if (!message.measurement || !message.field) return null;
  return {
    measurement: message.measurement,
    field: message.field,
    payload: message.payload == null ? null : message.payload,
  };
}

function probabilityVector(entry) {
  const candidate = entry.probs || entry.p_current || entry.probabilities;
  if (!Array.isArray(candidate) || candidate.length === 0) return null;
  const values = candidate.map(Number);
  if (values.some((value) => !Number.isFinite(value) || value < 0)) return null;
  return values;
}

function numericWindow(value) {
  if (value === "decay") return Number.POSITIVE_INFINITY;
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

export function flattenPredictionPayload(payload) {
  if (!payload || !payload.models || typeof payload.models !== "object") return [];
  const allowed = predictionsAllowed(payload);
  const mode = payload.mode === "demo" ? "demo" : "real";
  const rows = [];

  Object.entries(payload.models).forEach(([modelKey, model]) => {
    if (!model || typeof model !== "object") return;
    const windowKeys = Object.keys(model).filter((key) => /^\d+$/.test(key) || key === "decay");
    windowKeys.forEach((windowKey) => {
      const entry = model[windowKey];
      if (!entry || typeof entry !== "object") return;
      const nextBins = Array.isArray(entry.next_bins) ? entry.next_bins : [];
      const nextLabels = Array.isArray(entry.next_labels) ? entry.next_labels : [];
      const nextBin = entry.next_bin != null ? entry.next_bin : (nextBins[0] ?? null);
      const nextLabel = entry.next_label || nextLabels[0] || null;
      rows.push({
        modelKey,
        modelName: MODEL_DISPLAY[modelKey] || entry.model || modelKey,
        window: /^\d+$/.test(String(windowKey)) ? Number(windowKey) : windowKey,
        next_bin: allowed ? nextBin : null,
        label: allowed ? nextLabel : null,
        next_labels: allowed ? nextLabels.filter(Boolean) : [],
        phrase: allowed ? (entry.next_phrases && entry.next_phrases[0]) || null : null,
        confidence: allowed && entry.confidence != null && entry.confidence !== "" ? entry.confidence : null,
        probs: allowed ? probabilityVector(entry) : null,
        edges: Array.isArray(payload.bins) ? payload.bins.map(Number) : null,
        accuracy_hits: entry.accuracy_hits ?? null,
        accuracy_total: entry.accuracy_total ?? null,
        accuracy_pct: entry.accuracy_pct ?? null,
        win_accuracy_hits: entry.win_accuracy_hits ?? null,
        win_accuracy_total: entry.win_accuracy_total ?? null,
        win_accuracy_pct: entry.win_accuracy_pct ?? null,
        status: entry.status || null,
        mode,
        allowed,
      });
    });
  });

  return rows;
}

export function formatMetric(hits, total, pct, status) {
  if (status === "insufficient_history" || (hits == null && total == null)) {
    return "Insufficient history";
  }
  if (total == null || total === 0) {
    return "No qualifying predictions";
  }
  const percent = pct == null ? ((100 * hits) / total).toFixed(2) : Number(pct).toFixed(2);
  return `${hits}/${total} (${percent}%)`;
}

export function compareMetric(left, right, direction) {
  const missing = (value) => value == null || Number.isNaN(value);
  if (missing(left) && missing(right)) return 0;
  if (missing(left)) return 1;
  if (missing(right)) return -1;
  const delta = left - right;
  return direction === "asc" ? delta : -delta;
}

export function sortPredictionRows(rows, orderBy, orderDir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    if (orderBy === "window") {
      return compareMetric(numericWindow(a.window), numericWindow(b.window), orderDir);
    }
    return compareMetric(a[orderBy], b[orderBy], orderDir);
  });
  return copy;
}

const FAMILY_ORDER = ["decay_dirichlet", "sliding_window", "ewma"];

export function formatMultiplierRange(label) {
  if (typeof label !== "string") return null;
  const match = label.trim().match(/^\[([^,\]]+),\s*([^)\]]+)\)$/);
  if (!match) return null;
  const left = match[1].trim();
  const right = match[2].trim();
  if (!left || !right) return null;
  if (right === "∞" || right.toLowerCase() === "infinity") return `${left}x+`;
  return `${left}x–${right}x`;
}

export function displayRange(row) {
  if (!row || !row.allowed) return null;
  return formatMultiplierRange(row.label) || formatMultiplierRange(row.phrase) || null;
}

export function representativeWindow(rows) {
  const families = new Map();
  (rows || []).forEach((row) => {
    if (typeof row.window !== "number" || !Number.isFinite(row.window)) return;
    const key = row.window;
    if (!families.has(key)) families.set(key, new Set());
    families.get(key).add(row.modelKey);
  });
  let bestCount = 0;
  families.forEach((set) => {
    if (set.size > bestCount) bestCount = set.size;
  });
  if (bestCount === 0) return null;
  const candidates = [];
  families.forEach((set, window) => {
    if (set.size === bestCount) candidates.push(window);
  });
  candidates.sort((left, right) => left - right);
  return candidates[Math.floor((candidates.length - 1) / 2)];
}

export function summaryRows(rows) {
  const window = representativeWindow(rows);
  if (window == null) return [];
  return (rows || [])
    .filter((row) => row.window === window)
    .sort((left, right) => {
      const leftIndex = FAMILY_ORDER.indexOf(left.modelKey);
      const rightIndex = FAMILY_ORDER.indexOf(right.modelKey);
      return (leftIndex === -1 ? FAMILY_ORDER.length : leftIndex)
        - (rightIndex === -1 ? FAMILY_ORDER.length : rightIndex);
    });
}

export function modelConsensus(rows) {
  const none = { agreed: false, text: "No clear model consensus" };
  const selected = summaryRows(rows);
  if (selected.length < 2) return none;
  const groups = new Map();
  selected.forEach((row) => {
    if (!row.allowed || row.next_bin == null) return;
    const range = displayRange(row);
    if (!range) return;
    const key = String(row.next_bin);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ row, range });
  });
  let bestKey = null;
  let bestSize = 1;
  let tie = false;
  groups.forEach((group, key) => {
    if (group.length > bestSize) {
      bestKey = key;
      bestSize = group.length;
      tie = false;
    } else if (group.length === bestSize && bestKey != null) {
      tie = true;
    }
  });
  if (bestKey == null || tie || bestSize < 2) return none;
  const group = groups.get(bestKey);
  return {
    agreed: true,
    text: `${group.length} models agree on ${group[0].range}`,
    range: group[0].range,
    window: group[0].row.window,
    count: group.length,
    modelKeys: group.map((item) => item.row.modelKey),
    modelNames: group.map((item) => item.row.modelName),
    rows: group.map((item) => item.row),
  };
}

export function topWinAccuracyRows(rows, limit = 5) {
  return (rows || [])
    .filter((row) => (
      row.win_accuracy_total >= MIN_WIN_ACCURACY_SAMPLES
      && row.win_accuracy_pct != null
      && row.status !== "insufficient_history"
    ))
    .sort((left, right) => {
      const byRate = right.win_accuracy_pct - left.win_accuracy_pct;
      if (byRate !== 0) return byRate;
      return right.win_accuracy_total - left.win_accuracy_total;
    })
    .slice(0, limit);
}

function predictedLowerEdge(row) {
  if (Array.isArray(row.edges) && row.next_bin != null && row.edges[row.next_bin] != null) {
    const edge = Number(row.edges[row.next_bin]);
    if (Number.isFinite(edge)) return edge;
  }
  if (typeof row.label === "string") {
    const match = row.label.match(/^\[([^,\]]+)/);
    if (match) {
      const edge = Number(match[1]);
      if (Number.isFinite(edge)) return edge;
    }
  }
  return null;
}

export function thresholdOutlook(row) {
  if (!row || !row.allowed) return { available: false, locked: !row || !row.allowed };
  const edges = row.edges;
  const probs = row.probs;
  if (!Array.isArray(edges) || !Array.isArray(probs) || edges.length === 0 || edges.length !== probs.length) {
    return { available: false, locked: false };
  }
  const total = probs.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return { available: false, locked: false };
  const share = probs.map((value) => value / total);
  const chances = THRESHOLD_LEVELS.map((level) => ({
    level,
    probability: share.reduce((sum, value, index) => (
      Number(edges[index]) >= level ? sum + value : sum
    ), 0),
  }));
  const lowerEdge = predictedLowerEdge(row);
  const higher = lowerEdge == null ? chances : chances.filter((chance) => chance.level > lowerEdge);
  return {
    available: true,
    locked: false,
    mostLikely: displayRange(row),
    chances,
    strongHighThreshold: higher.some((chance) => chance.probability >= STRONG_THRESHOLD_PROBABILITY),
  };
}

export function formatChance(probability) {
  return `${(probability * 100).toFixed(1)}%`;
}

export function readPayloadTimestamp(payload) {
  if (!payload || typeof payload !== "object") return null;
  const value = payload.generated_at ?? payload.updated_at ?? payload.ts ?? null;
  if (value == null || value === "") return null;
  return value;
}

export function formatLocalTimestamp(value) {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function predictionStatusChips({ socketDown, hasRows, source }) {
  const chips = [];
  if (!socketDown && source === "socket" && hasRows) chips.push("Live");
  if (hasRows && (socketDown || source !== "socket")) chips.push("Cached");
  if (socketDown) chips.push("Disconnected");
  return chips;
}

export function formatConfidence(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value.toFixed(2);
  return String(value);
}

export function formatUpdatedAt(value) {
  return formatLocalTimestamp(value);
}
